import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { CONTENT } from "@/data/content";

const LanguageContext = createContext(null);
const STORAGE_KEY = "fr_lang";
const DEFAULT_LANG = "en";

function readStoredLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "es" || stored === "en" ? stored : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage unavailable (private mode, etc). Language just won't persist.
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next === "es" ? "es" : "en");
  }, []);

  const toggleLang = useCallback(() => {
    setLangState((l) => (l === "en" ? "es" : "en"));
  }, []);

  const value = useMemo(
    () => ({ lang, setLang, toggleLang, t: CONTENT[lang] }),
    [lang, setLang, toggleLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
  return ctx;
}
